import { useQuery } from "@tanstack/react-query";
import { UseFormRegister, UseFormReturn, useWatch } from "react-hook-form";

import localeCore from "../../core/locale.core";
import materialCore from "../../core/material.core";
import { useTranslation } from "../../core/translation.core";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { MaterialAttribute } from "../../types/material";
import { LocalInputDefaultLocale } from "../locale/locale.comp";
import { LabelAndInput } from "../reuseables/form.comp";
import { selectCss } from "../reuseables/native.comp";

interface AttributeProps {
  methods: UseFormReturn<{ attributes: MaterialAttribute[] }, any>;
}

export default function MaterialAttributeEditor(props: AttributeProps) {
  const materialAttributes = useWatch({
    name: "attributes",
    control: props.methods.control,
  });
  const { t, msg } = useTranslation();

  function addRow(e: any) {
    e.preventDefault();
    let attributes = props.methods.getValues("attributes");

    if (attributes === undefined) {
      attributes = [];
    }

    props.methods.setValue("attributes", [...attributes, { materialAttrDefId: null, name: null, type: null, value: null }]);
  }

  const rows = materialAttributes?.map((materialAttr, index) => (
    <AttributeRow key={index} materialAttr={materialAttr} register={props.methods.register} idx={index} />
  ));

  return (
    <div className="my-8 border p-4">
      <div className="block text-base font-semibold mb-2">{t(msg.attribute.attributes)}</div>

      <div id="attributes-list">
        <div>{rows}</div>
      </div>
      <button id="add-more" className="underline mt-4" onClick={(e) => addRow(e)}>
        {t(msg.product.addAttributes)}
      </button>
    </div>
  );
}

function useAttributes() {
  return useQuery({
    queryKey: ["material-attributes"],
    queryFn: materialCore.attributes.list,
  });
}

interface AttributeRowProps {
  materialAttr: MaterialAttribute;
  register: UseFormRegister<{ attributes: MaterialAttribute[] }>;
  idx: number;
}

function AttributeRow(props: AttributeRowProps) {
  const { data: attributes, isFetched, failureReason, isError } = useAttributes();

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (!isFetched) return <LoadingOvalCenterIcon />;
  if (!attributes) return <ErrorCenterIcon reason={{ message: "null" }} />;

  return (
    <div className="grid grid-cols-1 gap-6 sm:gap-0 sm:grid-cols-[150px_auto] items-start py-3 border-b border-her-gray">
      <div>
        <p className="text-[10px] mb-1">#{props.idx + 1}</p>
        <select className={`${selectCss} text-xs h-[40px]`} {...props.register(`attributes.${props.idx}.materialAttrDefId`)}>
          {attributes.map((attr) => (
            <option key={attr.id} value={attr.id}>
              {localeCore.getLocaleValueDefaultFallback(attr.name)}
            </option>
          ))}
        </select>
      </div>
      <ValueBox register={props.register} fieldName={`attributes.${props.idx}.value`} materialAttr={props.materialAttr} />
    </div>
  );
}

interface ValueBoxProps {
  register: UseFormRegister<{ attributes: MaterialAttribute[] }>;
  fieldName: string;
  materialAttr: MaterialAttribute;
}

const ValueBox = ({ register, fieldName, materialAttr }: ValueBoxProps) => {
  const { t, msg } = useTranslation();
  const { data: attributes, isFetched, failureReason, isError } = useAttributes();

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (!isFetched) return <LoadingOvalCenterIcon />;
  if (!attributes) return <ErrorCenterIcon reason={{ message: "null" }} />;

  if (!materialAttr) return null;

  const selectedAttr = attributes.find((a) => a.id === materialAttr.materialAttrDefId);

  if (!selectedAttr) return null;

  switch (selectedAttr.type) {
    case "string":
      return (
        <LocalInputDefaultLocale
          register={register}
          label=""
          fieldName={fieldName}
          labelCss="hidden"
          subLabelCss="text-[10px] mb-1"
        />
      );
    case "number":
      return (
        <LabelAndInput
          type="number"
          label={t(msg.attribute.number)}
          register={register}
          fieldName={fieldName}
          topLabelCss="block"
          labelCss="block text-[10px] mb-1"
        />
      );
  }
};
